import {
  useInfiniteScroll,
  UseInfiniteScrollParams,
  UseInfiniteScrollReturnType,
} from "shared/hooks/useInfiniteScroll.hook";
import {
  useAirSavedCardsInfiniteQuery,
  UseAirSavedCardsInfiniteQueryParams,
  UseGetSavedCardsInfiniteQueryReturnType,
} from "./useSavedCardsInfiniteQuery";

export type UseInfiniteScrollFetchParams<TElement extends HTMLElement> = Omit<
  UseInfiniteScrollParams<TElement>,
  "onReachEndOfList"
> &
  UseAirSavedCardsInfiniteQueryParams;

type UseInfiniteScrollFetchReturnType = Pick<UseInfiniteScrollReturnType, "hasScroll"> &
  UseGetSavedCardsInfiniteQueryReturnType & {
    requestAirMoreCards: () => void;
    shouldRenderAirMoreButton: boolean;
    shouldRenderAirInfiniteQueryLoader: boolean;
  };

export const useAirInfiniteScrollFetch = <TElement extends HTMLElement>({
  containerRef,
  onSuccess,
  onSettled,
  shouldRequestOnMount,
  queryKey,
  initialFilter,
  ...restProps
}: UseInfiniteScrollFetchParams<TElement>): UseInfiniteScrollFetchReturnType => {
  const queryMeta = useAirSavedCardsInfiniteQuery({
    onSuccess,
    onSettled,
    shouldRequestOnMount,
    queryKey,
    initialFilter,
    ...restProps,
  });
  const {
    requestMoreCards,
    canRequestMoreAirCards: canRequestMore,
    isCardsLoading,
    isCardsFetching,
    isCardsLoadingNextPage,
  } = queryMeta;

  const shouldRenderInfiniteQueryLoader = isCardsLoading || isCardsFetching || isCardsLoadingNextPage;

  const { hasScroll } = useInfiniteScroll<TElement>({
    containerRef,
    onReachEndOfList: () => {
      if (!canRequestMore || shouldRenderInfiniteQueryLoader) return;
      requestMoreCards();
    },
  });

  return {
    ...queryMeta,
    requestAirMoreCards: requestMoreCards,
    shouldRenderAirMoreButton: !hasScroll && canRequestMore && !shouldRenderInfiniteQueryLoader,
    shouldRenderAirInfiniteQueryLoader: shouldRenderInfiniteQueryLoader,
    hasScroll,
  };
};
